import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Wrench, Plus, Camera } from "@phosphor-icons/react";
import { api, money, FILE_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { toast } from "sonner";

const Stat = ({ label, value }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-3 text-center">
    <p className="font-heading font-black text-lg text-zinc-900">{value}</p>
    <p className="text-[10px] uppercase font-bold text-zinc-400">{label}</p>
  </div>
);

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [equip, setEquip] = useState([]);
  const [addEq, setAddEq] = useState(false);
  const [eForm, setEForm] = useState({ equipment_type: "", nickname: "", manufacturer: "", model: "", serial_number: "", physical_location: "" });
  const [histEq, setHistEq] = useState(null);
  const [hist, setHist] = useState(null);
  const eqPhotoRef = useRef();

  const uploadEqPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !histEq) return;
    const fd = new FormData();
    fd.append("file", f);
    await api.post(`/equipment/${histEq.id}/photos`, fd);
    setHistEq((await api.get(`/equipment/${histEq.id}`)).data);
    setEquip((await api.get(`/equipment?property_id=${id}`)).data);
    e.target.value = "";
    toast.success("Photo added");
  };

  const load = async () => {
    setData((await api.get(`/properties/${id}/history`)).data);
    setEquip((await api.get(`/equipment?property_id=${id}`)).data);
  };
  useEffect(() => { load(); }, [id]);

  const createEq = async () => {
    if (!eForm.nickname && !eForm.model) { toast.error("Add nickname or model"); return; }
    await api.post("/equipment", { ...eForm, property_id: id });
    setAddEq(false); setEForm({ equipment_type: "", nickname: "", manufacturer: "", model: "", serial_number: "", physical_location: "" });
    toast.success("Equipment added"); load();
  };

  const openHist = async (eq) => { const h = (await api.get(`/equipment/${eq.id}/history`)).data; setHist(h); setHistEq(eq); };

  if (!data) return <div className="p-6 text-zinc-400">Loading…</div>;
  const p = data.property || {}; const s = data.summary || {};
  return (
    <div className="min-h-screen pb-8">
      <header className="sticky top-0 z-30 bg-zinc-50/95 backdrop-blur border-b border-zinc-200 px-4 py-3 flex items-center gap-3">
        <button data-testid="prop-back-btn" onClick={() => nav(-1)} className="text-zinc-500"><ArrowLeft size={24} /></button>
        <div className="min-w-0"><h1 className="font-heading text-lg font-black tracking-tight truncate">{p.name || p.service_address}</h1>
          <p className="text-xs text-zinc-500 truncate">{[p.service_address, p.city, p.state, p.zip].filter(Boolean).join(", ")}</p></div>
      </header>

      <div className="p-4 space-y-5">
        <div className="grid grid-cols-3 gap-2" data-testid="property-summary">
          <Stat label="Jobs" value={s.total_jobs ?? 0} />
          <Stat label="Invoiced" value={money(s.total_invoiced)} />
          <Stat label="Paid" value={money(s.total_paid)} />
          <Stat label="Outstanding" value={money(s.outstanding)} />
          <Stat label="Last Service" value={s.last_service || "—"} />
          <Stat label="Next Recurring" value={s.next_recurring || "—"} />
        </div>

        <section>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400 flex items-center gap-1.5"><Wrench size={14} /> Equipment</h2>
            <button data-testid="prop-add-equipment-btn" className="text-xs font-bold text-orange-600" onClick={() => setAddEq(true)}>+ Add</button>
          </div>
          {addEq && (
            <div className="space-y-2 border border-dashed border-zinc-300 rounded-lg p-2.5 mb-2">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Nickname" value={eForm.nickname} onChange={(e) => setEForm({ ...eForm, nickname: e.target.value })} className="h-9" data-testid="pd-eq-nickname" />
                <Input placeholder="Type" value={eForm.equipment_type} onChange={(e) => setEForm({ ...eForm, equipment_type: e.target.value })} className="h-9" />
                <Input placeholder="Manufacturer" value={eForm.manufacturer} onChange={(e) => setEForm({ ...eForm, manufacturer: e.target.value })} className="h-9" />
                <Input placeholder="Model" value={eForm.model} onChange={(e) => setEForm({ ...eForm, model: e.target.value })} className="h-9" data-testid="pd-eq-model" />
              </div>
              <Button size="sm" className="h-9 w-full" data-testid="pd-save-eq-btn" onClick={createEq}>Save</Button>
            </div>
          )}
          <div className="space-y-2">
            {equip.length === 0 && <p className="text-sm text-zinc-400">No equipment recorded.</p>}
            {equip.map((eq) => (
              <button key={eq.id} data-testid={`equipment-row-${eq.id}`} onClick={() => openHist(eq)}
                className="w-full text-left bg-white border border-zinc-200 rounded-xl p-3">
                <p className="font-semibold text-zinc-900">{eq.nickname || eq.model || eq.equipment_type}</p>
                <p className="text-xs text-zinc-500">{[eq.manufacturer, eq.model, eq.serial_number && `SN ${eq.serial_number}`].filter(Boolean).join(" · ")}</p>
              </button>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">Service Timeline</h2>
          <div className="space-y-2" data-testid="property-timeline">
            {data.timeline.length === 0 && <p className="text-sm text-zinc-400">No jobs yet.</p>}
            {data.timeline.map((t) => (
              <button key={t.job_id} data-testid={`timeline-job-${t.job_id}`} onClick={() => nav(`/job/${t.job_id}`)}
                className="w-full text-left bg-white border border-zinc-200 rounded-xl p-3">
                <div className="flex justify-between"><span className="text-xs font-bold text-zinc-500">{t.date}</span>
                  {t.invoice_number && <span className="text-xs text-zinc-500">{t.invoice_number} · {money(t.invoice_total)} · {t.status}</span>}</div>
                <p className="text-sm text-zinc-900 mt-0.5">{t.work.join(", ")}</p>
                {t.materials.length > 0 && <p className="text-xs text-zinc-400 mt-0.5">Materials: {t.materials.join(", ")}</p>}
              </button>
            ))}
          </div>
        </section>
      </div>

      <Drawer open={!!histEq} onOpenChange={(o) => { if (!o) setHistEq(null); }}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">{histEq?.nickname || histEq?.model}</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-4 space-y-2 max-h-[60vh] overflow-y-auto" data-testid="equipment-history">
            <p className="text-xs text-zinc-500">{[histEq?.manufacturer, histEq?.model, histEq?.serial_number && `SN ${histEq?.serial_number}`].filter(Boolean).join(" · ")}</p>
            <div className="flex gap-2 flex-wrap" data-testid="equipment-photos">
              {(histEq?.photos || []).map((ph) => (
                <button key={ph.id} data-testid={`del-eq-photo-${ph.id}`}
                  onClick={async () => { await api.delete(`/equipment/${histEq.id}/photos/${ph.id}`); setHistEq((await api.get(`/equipment/${histEq.id}`)).data); setEquip((await api.get(`/equipment?property_id=${id}`)).data); }}
                  className="relative group" title="Tap to remove">
                  <img src={FILE_URL(ph.url)} alt="equipment" className="w-16 h-16 object-cover rounded-lg border border-zinc-200" />
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">×</span>
                </button>
              ))}
              <input ref={eqPhotoRef} type="file" accept="image/*" capture="environment" hidden onChange={uploadEqPhoto} data-testid="eq-photo-input" />
              <button data-testid="add-eq-photo-btn" onClick={() => eqPhotoRef.current?.click()} className="w-16 h-16 rounded-lg border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400"><Camera size={22} /></button>
            </div>
            {(hist?.timeline || []).length === 0 && <p className="text-sm text-zinc-400 py-4">No service history yet.</p>}
            {(hist?.timeline || []).map((t) => (
              <div key={t.job_id} className="border border-zinc-200 rounded-lg p-2.5">
                <span className="text-xs font-bold text-zinc-500">{t.date}{t.invoice_number ? ` · ${t.invoice_number}` : ""}</span>
                <p className="text-sm text-zinc-900">{t.work.join(", ")}</p>
                {t.materials.length > 0 && <p className="text-xs text-zinc-400">Parts: {t.materials.join(", ")}</p>}
              </div>
            ))}
          </div>
          <DrawerFooter><Button variant="ghost" onClick={() => setHistEq(null)}>Close</Button></DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
